import React, { useContext, useState } from 'react'
import PropTypes from 'prop-types'
import { WingsIcon } from '../icons'
import { WingsHeading } from '../typography'
import { WingsAccordionContext } from './WingsAccordion'
import './WingsDropdown.css'

/**
 * WingsDropdown Component
 * 
 * Collapsible accordion/dropdown component for content sections.
 * Uses native HTML details/summary for accessibility.
 * 
 * @component
 * @example
 * <WingsDropdown title="The Workplace">
 *   <p>Content goes here...</p>
 * </WingsDropdown>
 * 
 * @example
 * // With view more link
 * <WingsDropdown 
 *   title="The Prerequisites" 
 *   viewMoreText="VIEW MORE"
 *   viewMoreHref="/prerequisites"
 * >
 *   <p>Content goes here...</p>
 * </WingsDropdown>
 */
const WingsDropdown = ({
  title,
  children,
  viewMoreText,
  viewMoreHref,
  defaultOpen = false,
  accordionItemKey = null,
  openColor = '#ffffff',
  closedColor = '#ffffff',
  fontColor = 'var(--w-color-gray-900)',
  className = '',
  isWide = false,
  dropdownRef = null,
  ...props
}) => {
  const accordionContext = useContext(WingsAccordionContext)
  const [isOpen, setIsOpen] = useState(defaultOpen)

  const accordionOpen = accordionContext && accordionItemKey !== null
    ? accordionContext.activeItemKey === accordionItemKey
    : null

  const isDropdownOpen = accordionOpen !== null ? accordionOpen : isOpen

  const dropdownClasses = [
    'wings-dropdown',
    isDropdownOpen ? 'wings-dropdown--open' : '',
    className
  ].filter(Boolean).join(' ')

  const onHeaderClick = () => {
    if (accordionContext && accordionItemKey !== null) {
      accordionContext.setActiveItemKey((currentActiveItemKey) => (
        currentActiveItemKey === accordionItemKey ? null : accordionItemKey
      ))
      return
    }

    setIsOpen(!isOpen)
  }

  /**
   * Supports keyboard toggling for custom button-like header.
   * @param {React.KeyboardEvent<HTMLDivElement>} e
   */
  const onHeaderKeyDown = (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return
    e.preventDefault()
    onHeaderClick()
  }

  return (
    <div className={dropdownClasses} {...props} ref={dropdownRef}>
      <div
        className={`wings-dropdown-header w-px-2 w-py-${isWide ? "2" : "1"}`}
        role="button"
        tabIndex={0}
        aria-expanded={isDropdownOpen}
        onClick={onHeaderClick}
        onKeyDown={onHeaderKeyDown}
        style={{ '--closed-bg': closedColor, '--open-bg': openColor, 'color': fontColor }}
      >
        <WingsHeading level={5} className="wings-dropdown-title">{title}</WingsHeading>
        <WingsIcon icon="chevron-down" className="wings-dropdown-icon" color={fontColor} />
      </div>
      <div className="wings-dropdown-content-wrapper" inert={isOpen ? undefined : "true"}>
        <div
          className="wings-dropdown-content"
          style={{ '--open-bg': openColor, '--font-color': fontColor, 'color': fontColor }}
        >
          <div className="wings-dropdown-content-inner w-px-2 w-pb-2">
            {children}
            {viewMoreText && viewMoreHref && (
              <a href={viewMoreHref} className="wings-dropdown-link" style={{ color: fontColor }}>
                {viewMoreText}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

WingsDropdown.propTypes = {
  /** Title text displayed in the header */
  title: PropTypes.node.isRequired,
  
  /** Content to display when expanded */
  children: PropTypes.node.isRequired,
  
  /** Optional "View More" link text */
  viewMoreText: PropTypes.string,
  
  /** Optional "View More" link href */
  viewMoreHref: PropTypes.string,
  
  /** Whether the dropdown is open by default */
  defaultOpen: PropTypes.bool,

  /** Unique key used when the dropdown is managed inside WingsAccordion */
  accordionItemKey: PropTypes.string,
  
  /** Background color when dropdown is open */
  openColor: PropTypes.string,
  
  /** Background color when dropdown is closed */
  closedColor: PropTypes.string,

  /** Text and icon color */
  fontColor: PropTypes.string,

  /** Additional CSS classes */
  className: PropTypes.string,
}

export default WingsDropdown
